import { inspectionTitle, type AssignedInquiry, type PassageNoticeReason } from '@ghp/domain'
import { useToast } from '@ghp/ui'
import { useQueryClient } from '@tanstack/react-query'

import { useConnectionStatus } from '../../app/connection'
import { useTeamData } from '../../app/use-team-data'
import type { AuthStore } from '../auth/auth-store'
import { recordPassageNotice } from '../../offline/outbox'
import { PASSAGE_NOTICES_QUERY_KEY } from '../../offline/query-keys'
import { stampLabel } from './labels'

/**
 * Le geste « Avis de passage » de la pré-fiche, **le même depuis la liste et depuis l'agenda** :
 * deux écrans ouvrent la pré-fiche, et un avis offert d'un côté seulement ferait chercher le
 * bouton à l'agent devant la porte close.
 *
 * ⚠️ **Rien n'attend le réseau** : le constat s'écrit en local et part quand il peut. C'est le
 * seul geste de l'app qui se fasse devant une porte close, donc au fond d'une impasse — attendre
 * une réponse du serveur le rendrait inutilisable là où il sert (D22).
 *
 * Le toast est celui du prototype (`avisPassage`, `mocks/ghp-ct/app.js`) **quand la tablette est
 * en ligne** : l'avis part dans la passe qui suit. Hors ligne, « envoyé » serait faux — le titre
 * dit alors ce qui est vrai, et quand l'avis partira.
 */
export function usePassageNotice(
  auth: AuthStore,
  requestSync: () => Promise<void>,
): (inquiry: AssignedInquiry, reason: PassageNoticeReason, comment?: string) => void {
  const data = useTeamData()
  const queryClient = useQueryClient()
  const toast = useToast()
  const connection = useConnectionStatus(auth)

  return (inquiry, reason, comment) => {
    const noticedAt = new Date()

    void recordPassageNotice(
      data,
      inquiry.id,
      reason,
      noticedAt.toISOString(),
      comment ?? null,
    ).then(
      async () => {
        const stamp = stampLabel(noticedAt.toISOString()) ?? ''
        const where = `${inspectionTitle(inquiry, { short: true })} · ${stamp}`

        if (connection === 'online')
          toast('Avis de passage envoyé', `${where} · back-office notifié`)
        else toast('Avis de passage enregistré', `${where} · partira au retour du réseau`)

        await queryClient.invalidateQueries({ queryKey: PASSAGE_NOTICES_QUERY_KEY })
        // Une passe tout de suite plutôt qu'à la minute suivante : quand il y a du réseau,
        // l'agent voit l'avis partir avant d'avoir rangé la tablette.
        await requestSync()
      },
      (error: unknown) => {
        // L'écriture locale a échoué (stockage plein, base fermée) : la pré-fiche s'est déjà fermée,
        // et sans ce toast rien ne dirait à l'agent que son constat n'est pas enregistré (M21).
        console.error('[sync] avis de passage non enregistré sur la tablette', error)
        toast('Avis de passage non enregistré', 'L’écriture sur la tablette a échoué — réessayez')
      },
    )
  }
}
